import * as Q from './exact.js';
import {
  canon,
  evalM,
  factors,
  identity,
  isIdentityExpr,
  isMatName,
  isNumericLit,
  isSupAtom,
  MAT_NUM_RE,
  MatError,
  MatIncomplete,
  MatSyntaxError,
  isEntryAtom,
  matLineAtoms,
  matText,
  mulVals,
  namesIn,
  numOf,
  opCount,
  parseMatLine,
  powerFactors,
  sameMultiset,
  termValues,
  valEq,
  detOf,
  inverseOf,
} from './matrix.js';
const MATRIX_STEP_IDS = {
  substitute: 'matrix.substitute@1',
  power: 'matrix.power@1',
  identity: 'matrix.identity@1',
  product: 'matrix.product@1',
  entrywise: 'matrix.entrywise@1',
  transpose: 'matrix.transpose@1',
  inverse: 'matrix.inverse@1',
  inverseCheck: 'matrix.inverse_check@1',
  det: 'matrix.det@1',
  compute: 'matrix.compute@1',
  factor: 'matrix.factor@1',
  solve: 'matrix.solve@1',
  define: 'matrix.define@1',
  expand: 'matrix.expand@1',
  /** moteur v52 */
  entryProduct: 'matrix.entry_product@1',
  /** moteur v52 */
  entry: 'matrix.entry@1',
};
const STEP = {
  substitute: {
    label: 'Remplacer',
    hora: 1,
    why: 'Une matrice remplac\xE9e par sa valeur (ou une valeur reconnue).',
  },
  power: {
    label: '\xC9crire la puissance',
    hora: 1,
    why: 'Une puissance est un produit de facteurs \xE9gaux : A\xB2 = A \xD7 A, A\u2074 = A\xB2 \xD7 A\xB2.',
  },
  identity: {
    label: 'Multiplier par I',
    hora: 1,
    why: 'I est neutre : I \xD7 M = M \xD7 I = M, et I\u1D4F = I.',
  },
  product: {
    label: 'Ligne par colonne',
    hora: 2,
    why: 'Le coefficient (i, j) d\u2019un produit, c\u2019est la ligne i de la premi\xE8re matrice par la colonne j de la seconde : on multiplie terme \xE0 terme et on ajoute.',
  },
  entrywise: {
    label: 'Coefficient par coefficient',
    hora: 1,
    why: 'Une somme de matrices, ou un nombre fois une matrice, se calcule coefficient par coefficient.',
  },
  transpose: {
    label: 'Transposer',
    hora: 1,
    why: 'Les lignes de la matrice deviennent ses colonnes.',
  },
  inverse: {
    label: 'Inverse d\u2019une matrice 2 \xD7 2',
    hora: 3,
    why: 'Si det A \u2260 0 : A\u207B\xB9 = (1 \xF7 det A) \xD7 [d, \u2212b ; \u2212c, a] \u2014 on \xE9change a et d, on change le signe de b et c.',
  },
  inverseCheck: {
    label: 'V\xE9rifier l\u2019inverse',
    hora: 2,
    why: 'AM = I : M est l\u2019inverse de A.',
  },
  det: { label: 'D\xE9terminant', hora: 2, why: 'det [a, b ; c, d] = a \xD7 d \u2212 b \xD7 c.' },
  compute: { label: 'Calculer', hora: 1, why: 'Les calculs faits.' },
  factor: {
    label: 'Factoriser par un nombre',
    hora: 1,
    why: 'Tous les coefficients sont des multiples du m\xEAme nombre.',
  },
  solve: {
    label: 'Multiplier par l\u2019inverse',
    hora: 2,
    why: 'AX = B avec A inversible : X = A\u207B\xB9B \u2014 on multiplie \xE0 GAUCHE par A\u207B\xB9, l\u2019ordre compte.',
  },
  define: {
    label: 'Nommer un calcul',
    hora: 1,
    why: 'Un calcul interm\xE9diaire re\xE7oit un nom.',
  },
  expand: {
    label: 'D\xE9velopper',
    hora: 1,
    why: 'Le produit se distribue sur la somme en gardant l\u2019ordre des facteurs : A(A \u2212 2I) = A\xB2 \u2212 2A, et AI = A.',
  },
  entryProduct: {
    label: 'Ligne par colonne',
    hora: 2,
    why: 'Le coefficient (i, j) d\u2019un produit, c\u2019est la ligne i de la premi\xE8re matrice par la colonne j de la seconde : on multiplie terme \xE0 terme et on ajoute.',
  },
  entry: {
    label: 'Lire le coefficient',
    hora: 1,
    why: 'Le coefficient (i, j) se lit \xE0 la ligne i, colonne j de la matrice \xE9crite.',
  },
};
const CARD = 'Calcul matriciel';
function kmatLineAtoms(text) {
  return matLineAtoms(text);
}
function kmatLine(text) {
  return lineOf(kmatLineAtoms(text));
}
const lineOf = (atoms) => ({ dis: [{ sides: [{ k: 's', n: matText(atoms) }] }] });
function parseText(text) {
  return parseMatLine(kmatLineAtoms(text));
}
const strip = (e) => (e.k === 'paren' ? strip(e.e) : e);
function sig(e) {
  const pf = powerFactors(e);
  return pf ? pf.map((f) => (f.n === 1 ? f.base : `${f.base}^${f.n}`)).join('\xB7') : canon(e);
}
const prodOf = (fs) => fs.slice(1).reduce((l, r) => ({ k: 'mul', l, r }), fs[0]);
function createKMatState(model) {
  const spec = model.kernel;
  const env = /* @__PURE__ */ new Map();
  const objects = /* @__PURE__ */ new Map();
  const mk = (key, start, value, target2) => ({
    key: canon(key),
    start,
    value,
    form: null,
    written: /* @__PURE__ */ new Set([sig(key), sig(start)]),
    target: target2,
    relevant: true,
  });
  const givenLit = /* @__PURE__ */ new Set();
  const givenTexts = /* @__PURE__ */ new Set();
  for (const [name, text] of Object.entries(spec.matrices ?? {})) {
    const e = parseText(text);
    if (e.length !== 1) throw new Error(`Matrice ${name} : une seule expression attendue.`);
    const v = evalM(e[0], env);
    givenTexts.add(matText(kmatLineAtoms(`${name} = ${text}`)));
    const key = parseText(name);
    const k0 = strip(key[0]);
    if (key.length !== 1) throw new Error(`Nom de matrice illisible : ${name}`);
    if (k0.k !== 'name') {
      const base = strip(k0.k === 'pow' || k0.k === 'tr' ? k0.e : k0);
      if (base.k !== 'name') throw new Error(`Nom de matrice illisible : ${name}`);
      if (!env.has(base.n)) {
        if (k0.k !== 'pow' || k0.n !== -1 || v.t !== 'm')
          throw new Error(`${name} : donne d\u2019abord la matrice ${base.n}.`);
        const inv = inverseOf(v.a);
        if (!inv) throw new Error(`${name} n\u2019est pas inversible.`);
        env.set(base.n, { t: 'm', r: v.r, c: v.c, a: inv });
      }
      if (!valEq(evalM(k0, env), v)) throw new Error(`${name} ne correspond pas \xE0 ${base.n}.`);
      objects.set(canon(k0), mk(k0, e[0], v, false));
      continue;
    }
    env.set(name, v);
    if (strip(e[0]).k === 'lit') givenLit.add(name);
    else objects.set(name, mk(k0, e[0], v, false));
  }
  givenTexts.add(matText(kmatLineAtoms(spec.start)));
  const startMembers = parseText(spec.start);
  let equation = null;
  let target;
  const tgtExpr = parseText(spec.goal.target);
  if (tgtExpr.length !== 1) throw new Error('La cible est une matrice ou un calcul.');
  if (startMembers.length === 2) {
    const unknown = canon(tgtExpr[0]);
    const left = strip(startMembers[0]);
    if (left.k !== 'mul') throw new Error('\xC9quation attendue : AX = B ou XA = B.');
    const side = canon(left.r) === unknown ? 'left' : canon(left.l) === unknown ? 'right' : null;
    if (!side) throw new Error('L\u2019inconnue doit \xEAtre un facteur du produit : AX = B.');
    const coef = side === 'left' ? left.l : left.r;
    const c = evalM(coef, env);
    const b = evalM(startMembers[1], env);
    const inv = evalM({ k: 'pow', e: coef, n: -1, text: '\u207B\xB9' }, env);
    void c;
    const x = side === 'left' ? mulVals(inv, b) : mulVals(b, inv);
    env.set(unknown, x);
    equation = { coef, right: startMembers[1], side, unknown };
    target = mk(tgtExpr[0], tgtExpr[0], x, true);
    objects.set(unknown, target);
  } else {
    const start = startMembers[0];
    const value = evalM(start, env);
    target = mk(tgtExpr[0], start, value, true);
    objects.set(target.key, target);
    if (canon(start) !== target.key) {
      objects.set(canon(start), target);
      givenTexts.add(matText(kmatLineAtoms(`${spec.goal.target} = ${spec.start}`)));
    }
    const t = strip(tgtExpr[0]);
    if (t.k === 'name' && !env.has(t.n)) env.set(t.n, value);
  }
  const answer = parseText(spec.answer);
  const ks = {
    model,
    env,
    objects,
    target,
    equation,
    answerLast: answer[answer.length - 1],
    relevantKeys: /* @__PURE__ */ new Set([...objects.keys()]),
    traps: [],
    givenLit,
    givenTexts,
    lines: [],
    known: [],
    concluded: false,
    path: spec.path ?? [],
  };
  for (const t of ks.path) {
    try {
      const ms = parseText(t);
      ks.relevantKeys.add(canon(ms[0]));
    } catch {
      // ligne illisible : ignorée
    }
  }
  ks.traps = (model.traps ?? []).flatMap((t) => {
    try {
      const ms = parseText(t.line);
      let value = null;
      try {
        value = evalM(ms[ms.length - 1], env);
      } catch {
        value = null;
      }
      return [
        {
          key: canon(ms[0]),
          text: matText(kmatLineAtoms(t.line)),
          value,
          line: kmatLine(t.line),
          title: t.title,
          why: t.why,
        },
      ];
    } catch {
      return [];
    }
  });
  ks.known = [spec.answer, ...ks.path, ...(model.traps ?? []).map((t) => t.line)].flatMap((t) => {
    try {
      return [kmatLineAtoms(t)];
    } catch {
      return [];
    }
  });
  return ks;
}
function valOf(ks, e) {
  try {
    return evalM(e, ks.env);
  } catch {
    return null;
  }
}
function prodVal(ks, fs) {
  let v = { t: 's', q: Q.ONE };
  for (const f of fs) {
    const x = valOf(ks, f);
    if (!x) return null;
    try {
      v = mulVals(v, x);
    } catch {
      return null;
    }
  }
  return v;
}
function efactors(e) {
  return factors(e).flatMap((f) => {
    const x = strip(f);
    if (x.k === 'pow' && x.n >= 2 && x.n <= 12)
      return Array.from({ length: x.n }, () => strip(x.e));
    return [x];
  });
}
const pass = (...kinds) => ({ ok: true, kinds });
function knownEqual(ks, X0, V0) {
  const X = strip(X0);
  const V = strip(V0);
  const vx = valOf(ks, X);
  const vv = valOf(ks, V);
  if (!vx || !vv || !valEq(vx, vv)) return false;
  const sx = sig(X);
  const sv = sig(V);
  if (sx === sv) return true;
  const named = (N, L) =>
    isNumericLit(L) && ((N.k === 'name' && ks.givenLit.has(N.n)) || isIdentityExpr(N));
  if (named(X, V) || named(V, X)) return true;
  for (const o of new Set(ks.objects.values()))
    if (o.written.has(sx) && o.written.has(sv)) return true;
  return false;
}
function detKnown(ks, M) {
  const o = ks.objects.get(canon({ k: 'det', e: strip(M) }));
  return !!o && !!o.form && numOf(o.form) !== null;
}
function inverseFormula(ks, P0, N0) {
  const P = strip(P0);
  const N = strip(N0);
  if (P.k !== 'pow' || P.n !== -1) return 'no';
  const base = valOf(ks, P.e);
  if (!base || base.t !== 'm' || base.r !== 2 || base.c !== 2) return 'no';
  const [[a, b], [c, d]] = base.a;
  const adj = {
    t: 'm',
    r: 2,
    c: 2,
    a: [
      [d, Q.neg(b)],
      [Q.neg(c), a],
    ],
  };
  const det = detOf(base.a);
  const adjOk = (L) => {
    const v = valOf(ks, L);
    return !!v && v.t === 'm' && valEq(v, adj);
  };
  let formula = false;
  const fs = factors(N);
  const lits = fs.filter((f) => strip(f).k === 'lit');
  if (lits.length === 1 && fs.length > 1 && adjOk(lits[0])) {
    const k = prodVal(
      ks,
      fs.filter((f) => strip(f).k !== 'lit'),
    );
    if (k && k.t === 's' && Q.eq(Q.mul(k.q, det), Q.ONE)) formula = true;
  }
  if (N.k === 'div' && adjOk(N.l)) {
    const k = valOf(ks, N.r);
    if (k && k.t === 's' && Q.eq(k.q, det)) formula = true;
  }
  if (N.k === 'lit' && Q.eq(det, Q.ONE) && adjOk(N)) formula = true;
  if (!formula) return 'no';
  return detKnown(ks, P.e) ? 'ok' : 'needDet';
}
const RANK = { same: 0, ok: 1, inverse: 2, needDet: 3, no: 4 };
const worst = (a, b) => (RANK[a] >= RANK[b] ? a : b);
function subst(ks, P0, N0) {
  const P = strip(P0);
  const N = strip(N0);
  if (sig(P) === sig(N)) return 'same';
  if (P.k === 'mul' || N.k === 'mul') {
    const fp = efactors(P);
    const fn = efactors(N);
    if (fp.length > 10 || fn.length > 10) return 'no';
    const memo = /* @__PURE__ */ new Map();
    const go = (i, j) => {
      if (i === fp.length && j === fn.length) return 'same';
      if (i === fp.length || j === fn.length) return 'no';
      const key = `${i},${j}`;
      const m = memo.get(key);
      if (m) return m;
      let best = 'no';
      for (let a = 1; i + a <= fp.length; a++)
        for (let b = 1; j + b <= fn.length; b++) {
          if (a > 1 && b > 1) continue;
          const rp = fp.slice(i, i + a);
          const rn = fn.slice(j, j + b);
          let pair;
          if (a === 1 && b === 1) pair = subst(ks, rp[0], rn[0]);
          else if (knownEqual(ks, prodOf(rp), prodOf(rn))) pair = 'ok';
          else if (a === 1) {
            const inv2 = inverseFormula(ks, rp[0], prodOf(rn));
            pair = inv2 === 'ok' ? 'inverse' : inv2;
          } else pair = 'no';
          if (pair === 'no') continue;
          const rest = go(i + a, j + b);
          if (rest === 'no') continue;
          const r = worst(pair, rest);
          if (RANK[r] < RANK[best]) best = r;
        }
      memo.set(key, best);
      return best;
    };
    return go(0, 0);
  }
  if (P.k === N.k && (P.k === 'add' || P.k === 'sub' || P.k === 'div')) {
    const n = N;
    return worst(subst(ks, P.l, n.l), subst(ks, P.r, n.r));
  }
  if (P.k === N.k && (P.k === 'neg' || P.k === 'tr' || P.k === 'det')) return subst(ks, P.e, N.e);
  if (P.k === 'pow' && N.k === 'pow' && P.n === N.n) {
    const r = subst(ks, P.e, N.e);
    if (r !== 'no') return r;
  }
  if (P.k === 'lit' && N.k === 'lit') {
    if (P.rows.length !== N.rows.length || P.rows[0].length !== N.rows[0].length) return 'no';
    let out = 'same';
    for (let i = 0; i < P.rows.length; i++)
      for (let j = 0; j < P.rows[0].length; j++)
        out = worst(out, subst(ks, P.rows[i][j], N.rows[i][j]));
    return out;
  }
  if (knownEqual(ks, P, N)) return 'ok';
  const inv = inverseFormula(ks, P, N);
  return inv === 'ok' ? 'inverse' : inv;
}
function productExpansion(ks, P, N) {
  const fs = efactors(P);
  if (fs.length !== 2) return null;
  const L = valOf(ks, fs[0]);
  const R = valOf(ks, fs[1]);
  if (!L || !R || L.t !== 'm' || R.t !== 'm') return null;
  const n = strip(N);
  if (n.k !== 'lit' || n.rows.length !== L.r || n.rows[0].length !== R.c) return null;
  for (let i = 0; i < L.r; i++)
    for (let j = 0; j < R.c; j++) {
      const ex = [];
      for (let k = 0; k < L.c; k++) ex.push(Q.mul(L.a[i][k], R.a[k][j]));
      if (!termsMatch(n.rows[i][j], ex)) return { i, j };
    }
  return 'ok';
}
function termsMatch(e, ex) {
  const terms = termValues(e);
  if (!terms) return false;
  const nz = (xs) => xs.filter((x) => !Q.isZero(x));
  return sameMultiset(nz(terms), nz(ex)) && (terms.length > 1 || nz(ex).length <= 1);
}
function linearTerms(ks, e) {
  const x = strip(e);
  const scale = (ts, q) => (ts ? ts.map((t) => ({ k: Q.mul(t.k, q), v: t.v })) : null);
  switch (x.k) {
    case 'add':
    case 'sub': {
      const l = linearTerms(ks, x.l);
      const r = linearTerms(ks, x.r);
      if (!l || !r) return null;
      return [...l, ...(x.k === 'sub' ? scale(r, Q.neg(Q.ONE)) : r)];
    }
    case 'neg':
      return scale(linearTerms(ks, x.e), Q.neg(Q.ONE));
    case 'mul': {
      const l = valOf(ks, x.l);
      const r = valOf(ks, x.r);
      if (l?.t === 's' && r && r.t !== 's') return scale(linearTerms(ks, x.r), l.q);
      if (r?.t === 's' && l && l.t !== 's') return scale(linearTerms(ks, x.l), r.q);
      break;
    }
    case 'div': {
      const r = valOf(ks, x.r);
      if (r?.t === 's' && !Q.isZero(r.q)) return scale(linearTerms(ks, x.l), Q.div(Q.ONE, r.q));
      break;
    }
    default:
      break;
  }
  if (x.k !== 'name' && x.k !== 'lit') return null;
  const v = valOf(ks, x);
  return v && v.t !== 's' ? [{ k: Q.ONE, v }] : null;
}
const coefAt = (v, i, j) =>
  v.t === 'm' ? v.a[i][j] : v.t === 'id' ? (i === j ? v.s : Q.ZERO) : v.q;
function entrywiseExpansion(ks, P, N) {
  const n = strip(N);
  if (n.k !== 'lit') return false;
  const ts = linearTerms(ks, P);
  if (!ts || ts.length < 2) return false;
  const r = n.rows.length;
  const c = n.rows[0].length;
  if (ts.some((t) => t.v.t === 'm' && (t.v.r !== r || t.v.c !== c))) return false;
  for (let i = 0; i < r; i++)
    for (let j = 0; j < c; j++)
      if (
        !termsMatch(
          n.rows[i][j],
          ts.map((t) => Q.mul(t.k, coefAt(t.v, i, j))),
        )
      )
        return false;
  return true;
}
function nf(ks, e) {
  const x = strip(e);
  const one = (w, q = Q.ONE) => /* @__PURE__ */ new Map([[w, q]]);
  const add = (a, b, sign) => {
    const out = new Map(a);
    b.forEach((q, w) => {
      const v = Q.add(out.get(w) ?? Q.ZERO, sign === 1 ? q : Q.neg(q));
      if (Q.isZero(v)) out.delete(w);
      else out.set(w, v);
    });
    return out;
  };
  switch (x.k) {
    case 'num':
      return one('', x.q);
    case 'name':
      return one(isIdentityExpr(x) ? '' : x.n);
    case 'lit':
      return one(canon(x));
    case 'neg': {
      const a = nf(ks, x.e);
      return a ? add(/* @__PURE__ */ new Map(), a, -1) : null;
    }
    case 'add':
    case 'sub': {
      const a = nf(ks, x.l);
      const b = nf(ks, x.r);
      return a && b ? add(a, b, x.k === 'add' ? 1 : -1) : null;
    }
    case 'mul': {
      const a = nf(ks, x.l);
      const b = nf(ks, x.r);
      if (!a || !b || a.size * b.size > 64) return null;
      let out = /* @__PURE__ */ new Map();
      a.forEach((qa, wa) =>
        b.forEach((qb, wb) => {
          out = add(out, one([wa, wb].filter(Boolean).join('\xB7'), Q.mul(qa, qb)), 1);
        }),
      );
      return out;
    }
    case 'pow': {
      if (isIdentityExpr(x)) return one('');
      if (x.n < 2 || x.n > 6) return one(canon(x));
      let out = one('');
      for (let i = 0; i < x.n && out; i++)
        out = nf(ks, { k: 'mul', l: { k: 'paren', e: buildNf(out) }, r: x.e });
      return out;
    }
    case 'div': {
      const r = valOf(ks, x.r);
      const a = nf(ks, x.l);
      if (!a || !r || r.t !== 's' || Q.isZero(r.q)) return null;
      const out = /* @__PURE__ */ new Map();
      a.forEach((q, w) => out.set(w, Q.div(q, r.q)));
      return out;
    }
    case 'det': {
      const v = valOf(ks, x);
      return v && v.t === 's' ? one('', v.q) : null;
    }
    default:
      return one(canon(x));
  }
}
function buildNf(m) {
  const terms = [...m.entries()].map(([w, q]) => {
    const coef = { k: 'num', q, text: '' };
    if (!w) return coef;
    const ws = w.split('\xB7').map((n) => ({ k: 'name', n }));
    return { k: 'mul', l: coef, r: prodOf(ws) };
  });
  if (!terms.length) return { k: 'num', q: Q.ZERO, text: '0' };
  return terms.slice(1).reduce((l, r) => ({ k: 'add', l, r }), terms[0]);
}
const sameNf = (a, b) =>
  !!a && !!b && a.size === b.size && [...a].every(([w, q]) => !!b.get(w) && Q.eq(b.get(w), q));
function isLinear(ks, e) {
  return linearTerms(ks, e) !== null;
}
function hasMatrixProduct(ks, e) {
  const x = strip(e);
  switch (x.k) {
    case 'mul': {
      const l = valOf(ks, x.l);
      const r = valOf(ks, x.r);
      if (l && r && l.t !== 's' && r.t !== 's') return true;
      return hasMatrixProduct(ks, x.l) || hasMatrixProduct(ks, x.r);
    }
    case 'add':
    case 'sub':
    case 'div':
      return hasMatrixProduct(ks, x.l) || hasMatrixProduct(ks, x.r);
    case 'neg':
      return hasMatrixProduct(ks, x.e);
    case 'pow': {
      const v = valOf(ks, x.e);
      return !!v && v.t === 'm';
    }
    case 'tr':
    case 'det':
      return true;
    case 'entry':
      return hasMatrixProduct(ks, x.e);
    case 'lit':
      return x.rows.some((r) => r.some((c) => hasMatrixProduct(ks, c)));
    default:
      return false;
  }
}
function matrixNames(e, out = /* @__PURE__ */ new Set()) {
  const x = strip(e);
  if (x.k === 'det') return out;
  if (x.k === 'name') {
    if (!/^I[₀-₉]*$/.test(x.n)) out.add(x.n);
    return out;
  }
  if (x.k === 'lit') x.rows.forEach((r) => r.forEach((c) => matrixNames(c, out)));
  else if ('l' in x) {
    matrixNames(x.l, out);
    matrixNames(x.r, out);
  } else if ('e' in x) matrixNames(x.e, out);
  return out;
}
function dropIdentity(e) {
  const x = strip(e);
  if (x.k === 'mul') {
    const fs = factors(x);
    const keep = fs.filter((f) => !isIdentityExpr(f));
    const dropped = fs.length - keep.length;
    if (!keep.length) return { e: { k: 'name', n: 'I' }, dropped };
    return { e: prodOf(keep), dropped };
  }
  if (x.k === 'pow' && isIdentityExpr(x)) return { e: { k: 'name', n: 'I' }, dropped: 1 };
  return { e: x, dropped: 0 };
}
const closedForm = (e) => isNumericLit(e) || numOf(e) !== null || isIdentityExpr(e);
const ONE_STEP = {
  ok: false,
  title: 'Une \xE9tape \xE0 la fois',
  message:
    'Je ne vois pas comment on passe de ta ligne pr\xE9c\xE9dente \xE0 celle-ci : \xE9cris une \xE9tape de plus.',
};
const NEED_DET = {
  ok: false,
  title: 'Le d\xE9terminant d\u2019abord',
  message:
    'Calcule d\u2019abord le d\xE9terminant : il dit si la matrice est inversible, et par quoi diviser.',
};
function knownLiteral(ks, X0) {
  const X = strip(X0);
  if (isNumericLit(X) || isIdentityExpr(X)) return valOf(ks, X);
  if (X.k === 'name' && ks.givenLit.has(X.n)) return valOf(ks, X);
  const s = sig(X);
  for (const o of new Set(ks.objects.values()))
    if (o.form && (isNumericLit(o.form) || isIdentityExpr(o.form)) && o.written.has(s))
      return o.value;
  return null;
}
function entryStep(ks, P, N) {
  const n = numOf(N);
  if (n !== null && knownLiteral(ks, P.e)) return pass('entry');
  const fs = efactors(P.e);
  const i = P.i - 1;
  const j = P.j - 1;
  for (let s = 1; s < fs.length; s++) {
    const L = knownLiteral(ks, prodOf(fs.slice(0, s)));
    const R = knownLiteral(ks, prodOf(fs.slice(s)));
    if (!L || !R || L.t !== 'm' || R.t !== 'm' || L.c !== R.r) continue;
    if (i >= L.r || j >= R.c) continue;
    const ex = [];
    for (let k = 0; k < L.c; k++) ex.push(Q.mul(L.a[i][k], R.a[k][j]));
    if (opCount(N) > 0 && termsMatch(N, ex)) return pass('entryProduct');
  }
  if (n !== null && hasMatrixProduct(ks, P.e))
    return {
      ok: false,
      title: 'Montre le calcul',
      message: `\xC9cris le coefficient (${P.i}, ${P.j}) : la ligne ${P.i} de la premi\xE8re matrice par la colonne ${P.j} de la seconde, terme \xE0 terme.`,
    };
  return null;
}
function classify(ks, obj, P0, N0, depth = 0) {
  const P = strip(P0);
  const N = strip(N0);
  if (canon(P) === canon(N)) return pass();
  if (P.k === 'entry') {
    const c = entryStep(ks, P, N);
    if (c) return c;
  }
  const eq = ks.equation;
  if (eq && obj.target && canon(P) === eq.unknown) {
    const inv = { k: 'pow', e: eq.coef, n: -1, text: '\u207B\xB9' };
    const want =
      eq.side === 'left' ? { k: 'mul', l: inv, r: eq.right } : { k: 'mul', l: eq.right, r: inv };
    if (sig(N) === sig(want)) return pass('solve');
    return {
      ok: false,
      title: 'Isole X',
      message:
        eq.side === 'left'
          ? 'Pour isoler X dans AX = B, multiplie \xE0 gauche par A\u207B\xB9 : X = A\u207B\xB9B.'
          : 'Pour isoler X dans XA = B, multiplie \xE0 droite par A\u207B\xB9 : X = BA\u207B\xB9.',
    };
  }
  const pp = powerFactors(P);
  const pn = powerFactors(N);
  if (
    pp &&
    pn &&
    pp.length === pn.length &&
    pp.every((f, i) => f.base === pn[i].base && f.n === pn[i].n)
  )
    return pass('power');
  const dp = dropIdentity(P);
  const dn = dropIdentity(N);
  if (dp.dropped > dn.dropped && sig(dp.e) === sig(dn.e)) return pass('identity');
  const s = subst(ks, P, N);
  if (s === 'same') return pass();
  if (s === 'ok') return pass('substitute');
  if (s === 'inverse') return pass('inverse');
  if (s === 'needDet') return NEED_DET;
  if (P.k === 'pow' && P.n === -1) {
    const base = valOf(ks, P.e);
    const nv = valOf(ks, N);
    if (nv && base) {
      const isI = (e) => {
        if (isIdentityExpr(e)) return true;
        const v = valOf(ks, e);
        return !!v && isNumericLit(e) && valEq(v, { t: 'id', s: Q.ONE });
      };
      const checked = ks.lines.some(({ members }) => {
        const head = efactors(members[0]);
        if (head.length !== 2) return false;
        const h0 = valOf(ks, head[0]);
        const h1 = valOf(ks, head[1]);
        if (!h0 || !h1) return false;
        const pair = (valEq(h0, base) && valEq(h1, nv)) || (valEq(h0, nv) && valEq(h1, base));
        return pair && isI(members[members.length - 1]);
      });
      if (checked) return pass('inverseCheck');
      if (closedForm(N))
        return base.t === 'm' && base.r === 2 && !detKnown(ks, P.e)
          ? NEED_DET
          : {
              ok: false,
              title: 'Montre le calcul',
              message:
                base.t === 'm' && base.r === 2
                  ? '\xC9cris l\u2019inverse avec la formule : (1 \xF7 det A) \xD7 [d, \u2212b ; \u2212c, a].'
                  : 'V\xE9rifie d\u2019abord que le produit de la matrice par celle que tu proposes donne I.',
            };
    }
  }
  if (P.k === 'det') {
    const m = valOf(ks, P.e);
    if (m && m.t === 'm' && m.r === 2 && m.c === 2) {
      const ex = [Q.mul(m.a[0][0], m.a[1][1]), Q.neg(Q.mul(m.a[0][1], m.a[1][0]))];
      if (opCount(N) > 0 && termsMatch(N, ex)) return pass('det');
      if (numOf(N) !== null)
        return {
          ok: false,
          title: 'Montre le calcul',
          message: '\xC9cris le calcul du d\xE9terminant : a \xD7 d \u2212 b \xD7 c.',
        };
    }
  }
  if (P.k === 'tr' && N.k === 'lit' && isNumericLit(N)) {
    if (strip(P.e).k !== 'lit' && !(strip(P.e).k === 'name')) return ONE_STEP;
    return pass('transpose');
  }
  const pe = productExpansion(ks, P, N);
  if (pe === 'ok') return pass('product');
  if (pe && isNumericLit(N))
    return {
      ok: false,
      title: 'Montre le calcul',
      message: '\xC9cris le produit ligne par colonne avant de donner le r\xE9sultat.',
    };
  if (pe)
    return {
      ok: false,
      title: 'Montre le calcul',
      message: `\xC9cris le coefficient (${pe.i + 1}, ${pe.j + 1}) : la ligne ${pe.i + 1} de la premi\xE8re matrice par la colonne ${pe.j + 1} de la seconde, terme \xE0 terme.`,
    };
  if (entrywiseExpansion(ks, P, N)) return pass('entrywise');
  const np = nf(ks, P);
  if (np && [...np.keys()].some((w) => w !== '') && sameNf(np, nf(ks, N))) return pass('expand');
  if (depth < 4) {
    if (P.k === N.k && (P.k === 'add' || P.k === 'sub')) {
      const n = N;
      const l = classify(ks, obj, P.l, n.l, depth + 1);
      const r = classify(ks, obj, P.r, n.r, depth + 1);
      if (!l.ok) return l;
      if (!r.ok) return r;
      return pass(...l.kinds, ...r.kinds);
    }
    if (P.k === 'mul' || N.k === 'mul') {
      const fp = efactors(P);
      const fn = efactors(N);
      let i = 0;
      while (i < fp.length - 1 && i < fn.length - 1 && sig(fp[i]) === sig(fn[i])) i++;
      let j = 0;
      while (
        j < fp.length - 1 - i &&
        j < fn.length - 1 - i &&
        sig(fp[fp.length - 1 - j]) === sig(fn[fn.length - 1 - j])
      )
        j++;
      const mp = fp.slice(i, fp.length - j);
      const mn = fn.slice(i, fn.length - j);
      if (i + j > 0 && mp.length && mn.length)
        return classify(ks, obj, prodOf(mp), prodOf(mn), depth + 1);
    }
  }
  if (P.k === 'lit' && N.k === 'lit' && opCount(N) < opCount(P)) return pass('compute');
  if (N.k === 'lit' && isLinear(ks, P) && !isNumericLit(P)) {
    const names = [...matrixNames(P)];
    if (names.length)
      return {
        ok: false,
        title: 'Montre le calcul',
        message: `Remplace d\u2019abord ${names.join(', ')} par sa matrice, puis calcule coefficient par coefficient.`,
      };
    return pass('entrywise');
  }
  if (P.k === 'lit' && N.k === 'mul') {
    const fs = factors(N);
    if (fs.length === 2 && numOf(fs[0]) !== null && strip(fs[1]).k === 'lit') return pass('factor');
  }
  if (
    !hasMatrixProduct(ks, P) &&
    [...namesIn(N)].every((n) => namesIn(P).has(n)) &&
    [...matrixNames(P)].every((n) => matrixNames(N).has(n)) &&
    opCount(N) < opCount(P)
  )
    return pass('compute');
  if (closedForm(N) && hasMatrixProduct(ks, P))
    return {
      ok: false,
      title: 'Montre le calcul',
      message: '\xC9cris le produit ligne par colonne avant de donner le r\xE9sultat.',
    };
  return ONE_STEP;
}
function isRelevant(ks, head) {
  if (ks.relevantKeys.has(canon(strip(head)))) return true;
  const st = strip(ks.target.start);
  if (st.k === 'pow' && st.n === -1) {
    const fs = efactors(head);
    if (fs.length === 2 && fs.some((f) => sig(f) === sig(st.e))) return true;
  }
  const pf = powerFactors(head);
  if (!pf || pf.length !== 1) return false;
  const want = powerFactors(ks.target.start);
  return !!want && want.some((f) => f.base === pf[0].base && f.n >= pf[0].n);
}
function findObject(ks, head0) {
  const head = strip(head0);
  const obj = ks.objects.get(canon(head));
  if (obj) return obj;
  const h = sig(head);
  for (const o of new Set(ks.objects.values())) if (o.form && sig(o.form) === h) return o;
  return void 0;
}
function strictPrefixOfKnown(ks, A) {
  return ks.known.some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
}
function wrongWhy(ks, obj, v) {
  const want = obj.value;
  const st = strip(obj.start);
  if (st.k === 'entry' && v.t === 's') {
    const pw = strip(st.e);
    if (pw.k === 'pow' && pw.n >= 2) {
      const b = valOf(ks, { k: 'entry', e: pw.e, i: st.i, j: st.j });
      if (b && b.t === 's' && Q.eq(b.q, v.q) && !valEq(b, want))
        return {
          title: 'La puissance',
          message: `C\u2019est le coefficient de la matrice elle-m\xEAme : il faut celui de sa puissance ${pw.text}.`,
        };
    }
  }
  if (want.t === 'm' && v.t === 'm' && (want.r !== v.r || want.c !== v.c))
    return {
      title: 'Pas la bonne taille',
      message: `Le r\xE9sultat est une matrice ${want.r} \xD7 ${want.c} (${want.r} ligne${want.r > 1 ? 's' : ''}, ${want.c} colonne${want.c > 1 ? 's' : ''}).`,
    };
  const fs = efactors(obj.start);
  if (fs.length === 2) {
    const a = valOf(ks, fs[0]);
    const b = valOf(ks, fs[1]);
    if (a && b && a.t === 'm' && b.t === 'm') {
      try {
        if (valEq(v, mulVals(b, a)) && !valEq(v, want))
          return {
            title: 'Pas le bon ordre',
            message:
              'Le produit de matrices d\xE9pend de l\u2019ordre : tu as calcul\xE9 le produit dans l\u2019autre sens.',
          };
      } catch {
        // ligne illisible : ignorée
      }
      if (a.r === b.r && a.c === b.c && v.t === 'm' && v.r === a.r && v.c === a.c) {
        const had = a.a.map((row, i) => row.map((x, j) => Q.mul(x, b.a[i][j])));
        if (valEq(v, { t: 'm', r: a.r, c: a.c, a: had }))
          return {
            title: 'Ligne par colonne',
            message:
              'Un produit de matrices ne se calcule pas coefficient par coefficient : c\u2019est ligne par colonne.',
          };
      }
    }
  }
  if (want.t === 'm' && v.t === 'm') {
    for (let i = 0; i < want.r; i++)
      for (let j = 0; j < want.c; j++)
        if (!Q.eq(want.a[i][j], v.a[i][j]))
          return {
            title: 'Calcul faux',
            message: `Le coefficient (${i + 1}, ${j + 1}) ne va pas.`,
          };
  }
  if (want.t === 'id' && v.t === 'm')
    return { title: 'Calcul faux', message: 'Ce n\u2019est pas la bonne matrice.' };
  return { title: 'Calcul faux', message: 'Ce n\u2019est pas la bonne valeur.' };
}
function kmatJudge(ks, chips, opts) {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = strictPrefixOfKnown(ks, A);
  let members;
  try {
    members = parseMatLine(A);
  } catch (e) {
    if (e instanceof MatIncomplete || (prefix && !opts?.verify)) {
      if (opts?.verify)
        return {
          kind: 'syntax',
          atoms: A,
          title: 'Ligne inachev\xE9e',
          message: 'Termine ta ligne.',
        };
      return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
    }
    return {
      kind: 'syntax',
      atoms: A,
      title: '\xC7a ne se lit pas',
      message: e instanceof MatSyntaxError ? e.message : String(e),
      badChip: chips.length - 1,
    };
  }
  if (members.length < 2) {
    if (opts?.verify)
      return {
        kind: 'syntax',
        atoms: A,
        line: lineOf(A),
        title: 'Une \xE9galit\xE9',
        message: '\xC9cris une \xE9galit\xE9 : \xAB AB = \u2026 \xBB.',
      };
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  }
  const v = judgeParsed(ks, A, members);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}
function judgeParsed(ks, A, members) {
  const line = lineOf(A);
  const base = { atoms: A, line };
  if (ks.givenTexts.has(matText(A)))
    return {
      kind: 'useless',
      ...base,
      title: 'C\u2019est l\u2019\xE9nonc\xE9',
      message: 'Cette ligne est d\xE9j\xE0 dans l\u2019\xE9nonc\xE9 : \xE9cris le calcul suivant.',
    };
  const head = strip(members[0]);
  const key = canon(head);
  let obj = findObject(ks, head);
  let define;
  let seq;
  const steps = [];
  if (obj) seq = [obj.form ?? obj.start, ...members.slice(1)];
  else if (head.k === 'name' && !ks.env.has(head.n) && !/^I/.test(head.n)) {
    if (closedForm(members[1]))
      return {
        kind: 'unjustified',
        ...base,
        title: 'D\u2019o\xF9 vient-elle ?',
        message: `Un nouveau nom se d\xE9finit par un calcul : \xE9cris d\u2019abord ce que vaut ${head.n} avec les matrices de l\u2019\xE9nonc\xE9.`,
      };
    let v;
    try {
      v = evalM(members[1], ks.env);
    } catch (e) {
      return { kind: 'false', ...base, title: 'Calcul impossible', message: e.message };
    }
    define = { name: head.n, value: v };
    obj = {
      key,
      start: members[1],
      value: v,
      form: null,
      written: /* @__PURE__ */ new Set([sig(head)]),
      target: false,
      relevant: isRelevant(ks, head),
    };
    seq = members.slice(1);
    steps.push('define');
  } else {
    let v;
    try {
      v = evalM(head, ks.env);
    } catch (e) {
      return trapOr(ks, key, null, base, {
        title:
          e instanceof MatError && e.code === 'size'
            ? 'Les tailles ne vont pas'
            : 'Calcul impossible',
        message: e.message,
      });
    }
    obj = {
      key,
      start: head,
      value: v,
      form: null,
      written: /* @__PURE__ */ new Set([sig(head)]),
      target: false,
      relevant: isRelevant(ks, head),
    };
    seq = members;
  }
  for (const m of members.slice(define ? 1 : 0)) {
    let v;
    try {
      v = evalM(m, ks.env);
    } catch (e) {
      return trapOr(ks, obj.key, null, base, {
        title:
          e instanceof MatError && e.code === 'size'
            ? 'Les tailles ne vont pas'
            : 'Calcul impossible',
        message: e.message,
      });
    }
    if (!valEq(v, obj.value)) return trapOr(ks, obj.key, v, base, wrongWhy(ks, obj, v));
  }
  if (!obj.relevant)
    return {
      kind: 'useless',
      ...base,
      title: 'Juste, mais \xE0 c\xF4t\xE9',
      message: 'C\u2019est juste, mais ce calcul ne sert pas pour la question.',
    };
  for (let i = 0; i + 1 < seq.length; i++) {
    const c = classify(ks, obj, seq[i], seq[i + 1]);
    if (!c.ok)
      return {
        kind: 'unjustified',
        ...base,
        title: c.title,
        message: c.message,
      };
    steps.push(...c.kinds);
  }
  const last = members[members.length - 1];
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: 'Rien de nouveau',
      message: 'Cette ligne redit ce qui est d\xE9j\xE0 \xE9crit.',
    };
  const final =
    obj.target &&
    !ks.concluded &&
    (sig(last) === sig(ks.answerLast) || (closedFormValue(ks.answerLast) && closedFormValue(last)));
  const hora = steps.reduce((s, k) => s + STEP[k].hora, 0);
  const main = steps.reduce((m, k) => (STEP[k].hora >= STEP[m].hora ? k : m), steps[0]);
  const why =
    [...new Set(steps)].map((k) => STEP[k].why).join(' ') +
    (final ? ' C\u2019est la r\xE9ponse.' : '');
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    rule: { id: MATRIX_STEP_IDS[main], card: CARD, label: STEP[main].label, hora, why },
    mmove: { key: obj.key, members, ...(define ? { define } : {}), ...(final ? { final } : {}) },
  };
}
const closedFormValue = (e) => isNumericLit(e) || numOf(e) !== null || isIdentityExpr(e);
function trapOr(ks, key, v, base, why) {
  const text = matText(base.atoms);
  const t = ks.traps.find(
    (t2) => t2.text === text || (t2.key === key && !!v && !!t2.value && valEq(v, t2.value)),
  );
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: t.title,
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
    };
  return { kind: 'false', ...base, title: why.title, message: why.message };
}
function kmatCommit(ks, v) {
  const m = v.mmove;
  if (!m) return;
  let obj = ks.objects.get(m.key);
  if (m.define) {
    ks.env.set(m.define.name, m.define.value);
    if (!obj) {
      obj = {
        key: m.key,
        start: m.members[1],
        value: m.define.value,
        form: null,
        written: /* @__PURE__ */ new Set(),
        target: false,
        relevant: true,
      };
      ks.objects.set(m.key, obj);
    }
  }
  if (!obj) obj = findObject(ks, m.members[0]);
  if (!obj) {
    const head = m.members[0];
    obj = {
      key: m.key,
      start: head,
      value: evalM(head, ks.env),
      form: null,
      written: /* @__PURE__ */ new Set(),
      target: false,
      relevant: true,
    };
    ks.objects.set(m.key, obj);
  }
  m.members.forEach((x) => obj.written.add(sig(x)));
  obj.form = m.members[m.members.length - 1];
  ks.lines.push({ key: obj.key, members: m.members });
  if (m.final) ks.concluded = true;
}
function kmatGoalReached(ks) {
  return ks.concluded;
}
function readText(text) {
  try {
    return parseText(text);
  } catch {
    return null;
  }
}
function kmatHolds(ks, text) {
  const ms = readText(text);
  if (!ms || ms.length < 2) return false;
  try {
    const head = strip(ms[0]);
    const obj = findObject(ks, head);
    const ref = obj ? obj.value : evalM(head, ks.env);
    return ms.every((m) => valEq(evalM(m, ks.env), ref));
  } catch {
    return false;
  }
}
function kmatIsAnswer(ks, text) {
  const ms = readText(text);
  if (!ms || ms.length < 2) return false;
  const obj = findObject(ks, ms[0]);
  if (!obj || obj !== ks.target) return false;
  try {
    return ms.slice(1).every((m) => valEq(evalM(m, ks.env), ks.target.value));
  } catch {
    return false;
  }
}
function kmatEstablished(ks, text) {
  const ms = readText(text);
  if (!ms || ms.length < 2) return false;
  const obj = findObject(ks, ms[0]);
  return !!obj && obj.written.has(sig(ms[ms.length - 1]));
}
const chipsOf = (atoms) => atoms.map((a) => ({ atoms: [a], text: a }));
function kmatNextMove(ks) {
  if (ks.concluded) return null;
  for (const t of ks.path) {
    if (kmatEstablished(ks, t)) continue;
    const atoms = kmatLineAtoms(t);
    const v = kmatJudge(ks, chipsOf(atoms), { verify: true });
    return { line: kmatLine(t), text: matText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}
function kmatSolvePath(model) {
  const ks = createKMatState(model);
  const lines = [];
  for (const t of ks.path) {
    const atoms = kmatLineAtoms(t);
    const v = kmatJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    kmatCommit(ks, v);
    lines.push({ line: kmatLine(t), text: matText(atoms), atoms, v });
  }
  return { lines, reached: ks.concluded };
}
const MATRIX_OPS = [
  '[',
  ']',
  ',',
  ';',
  '+',
  '\u2212',
  '\xD7',
  '\xF7',
  '(',
  ')',
  '\xB2',
  '\u207B\xB9',
  '\u1D40',
  '=',
];
function kmatEntryTexts(atoms) {
  const out = [];
  atoms.forEach((a, k) => {
    if (!isEntryAtom(a) || atoms[k - 1] !== ')') return;
    let depth = 0;
    let o = k - 1;
    for (; o >= 0; o--) {
      if (atoms[o] === ')') depth++;
      if (atoms[o] === '(') depth--;
      if (depth === 0) break;
    }
    if (o < 0) return;
    const t = matText(atoms.slice(o, k + 1));
    if (!out.includes(t)) out.push(t);
  });
  return out;
}
function kmatPaletteParts(model) {
  const spec = model.kernel;
  const texts = [
    spec.start,
    ...(spec.path ?? []),
    spec.answer,
    ...Object.values(spec.matrices ?? {}),
    ...(model.traps ?? []).map((t) => t.line),
    ...(model.choices ?? []).flatMap((c) => (c.line ? [c.line] : [])),
  ];
  const atoms = texts.flatMap((t) => {
    try {
      return kmatLineAtoms(t);
    } catch {
      return [];
    }
  });
  const names = [];
  const add = (n) => {
    if (!names.includes(n)) names.push(n);
  };
  Object.keys(spec.matrices ?? {}).forEach(add);
  atoms.filter(isMatName).forEach(add);
  const words = atoms.includes('det') ? ['det'] : [];
  const nums = [...new Set(atoms.filter((a) => MAT_NUM_RE.test(a)))];
  const val = (s) => Number(s.replace('\u2212', '-').replace(',', '.'));
  nums.sort((a, b) => val(a) - val(b) || a.localeCompare(b));
  const used = new Set(atoms);
  const sups = [
    ...new Set(atoms.filter((a) => isSupAtom(a) && !['\xB2', '\u207B\xB9', '\u1D40'].includes(a))),
  ];
  const ops = [...MATRIX_OPS.filter((o) => used.has(o) || o === '='), ...sups];
  return { names, words, numbers: nums, ops, entries: kmatEntryTexts(atoms) };
}
const _identity = identity;
export {
  MATRIX_OPS,
  MATRIX_STEP_IDS,
  _identity,
  createKMatState,
  kmatCommit,
  kmatEntryTexts,
  kmatEstablished,
  kmatGoalReached,
  kmatHolds,
  kmatIsAnswer,
  kmatJudge,
  kmatLine,
  kmatLineAtoms,
  kmatNextMove,
  kmatPaletteParts,
  kmatSolvePath,
  sig,
};
